import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import { useSelector } from "react-redux";
import { v4 as uuid } from "uuid";

import { tPdTooltipDirection, tPdTooltipMeasureValues } from "src/components/overlay";
import { closeModal, openModal, openModalAsync } from "src/redux/modals/actions";
import { tModalComponentProps, tModalResponse } from "src/redux/modals/types";
import { kColors } from "src/kColors";
import { useAppDispatch } from "src/redux";

export type tUseModal<P = void, D = void> = {
  close: (id: string) => void;
  open: (props?: P) => string;
  openAsync: (props?: P) => Promise<tModalResponse<D>>;
};

export const useModal = <P extends unknown = void, D extends unknown = void>(
  component: FC<tModalComponentProps<P, D>>,
): tUseModal<P, D> => {
  const dispatch = useAppDispatch();

  const close = useCallback(
    (modalId) => {
      dispatch(closeModal(modalId));
    },
    [dispatch],
  );

  const open = useCallback(
    (props) => {
      const id = uuid();
      dispatch(
        openModal({ id, component, props: { ...props, onResolve: () => close(id), onReject: () => close(id) } }),
      );
      return id;
    },
    [close, component, dispatch],
  );

  const openAsync = useCallback(
    (props) => {
      return dispatch(openModalAsync(component, props));
    },
    [component, dispatch],
  );

  return useMemo(() => ({ close, open, openAsync }), [close, open, openAsync]);
};

const ModalsRenderer = () => {
  const dispatch = useAppDispatch();
  const modals = useSelector((state) => state.ModalsReducer.modals);

  const onClose = (id: string) => {
    dispatch(closeModal(id));
  };

  return (
    <>
      {modals.map((modal) => {
        const ModalComponent = modal.component;
        const { onResolve, onReject, ...props } = modal.props;
        return (
          <Fragment key={modal.id}>
            <ModalComponent
              {...props}
              onClose={(data) => {
                onResolve?.({ data, status: "closed" });
                onClose(modal.id);
              }}
              onResolve={(data) => {
                onResolve?.({ data, status: "success" });
                onClose(modal.id);
              }}
              onReject={(error) => {
                onReject?.(error);
                onClose(modal.id);
              }}
            />
          </Fragment>
        );
      })}
    </>
  );
};

const getTooltipStyles = (
  referenceMeasureValues: tPdTooltipMeasureValues,
  tooltipMeasureValues: tPdTooltipMeasureValues,
  direction: tPdTooltipDirection,
  offset: number,
): StyleProp<ViewStyle> => {
  let positionStyles: ViewStyle = {
    left: referenceMeasureValues.x,
    top: referenceMeasureValues.y + referenceMeasureValues.height + offset,
  };

  if (direction === "top-left") {
    positionStyles = {
      left: referenceMeasureValues.x - tooltipMeasureValues.width + referenceMeasureValues.width,
      top: referenceMeasureValues.y - tooltipMeasureValues.height - offset,
    };
  }

  return [
    {
      position: "absolute",
      paddingHorizontal: 15,
      paddingVertical: 10,
      backgroundColor: kColors.brandDark,
      maxWidth: 200,
    },
    positionStyles,
  ];
};

const TooltipRenderer = () => {
  const tooltipRef = useRef<View>(null);
  const tooltip = useSelector((state) => state.ModalsReducer.tooltip);
  const [tooltipMeasureValues, setTooltipMeasureValues] = useState<tPdTooltipMeasureValues>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (tooltip.isOpen) {
      tooltipRef.current?.measureInWindow((x, y, width, height) => {
        setTooltipMeasureValues({ x, y, width, height });
      });
    }
  }, [tooltip.isOpen]);

  if (!tooltip.isOpen || !tooltip.direction || !tooltip.referenceMeasureValues) {
    return null;
  }

  return (
    <View
      ref={tooltipRef}
      style={getTooltipStyles(tooltip.referenceMeasureValues, tooltipMeasureValues, tooltip.direction, 5)}
    >
      {tooltip.render?.()}
    </View>
  );
};

const OverlayProvider: FC = ({ children }) => {
  return (
    <>
      {children}
      <ModalsRenderer />
      <TooltipRenderer />
    </>
  );
};

export default OverlayProvider;
